import camelCase from 'lodash/camelCase';
import { Component } from 'react';
import { reaction } from 'mobx';
import { inject } from 'mobx-react';
import ConsoleStore from '../stores/ConsoleStore';
import CustomizeUIStore from '../stores/CustomizeUI/CustomizeUIStore';
import EmailRegStore from '../stores/EmailRegStore';
import IpRegStore from '../stores/IpRegStore';
import ReportingApiStore from '../stores/ReportingApiStore';
import PolicyStore from '../stores/Policy/PolicyStore';
import AuthenticationConfigurationStore
    from '../stores/AuthenticationConfigurationSSO/AuthenticationConfigurationStore';
import BrowserBarOptionsStore from '../stores/BrowserBarOptionsStore';
import CustomPagesStore from '../stores/CustomPagesStore';
import LocalAccountsStore from '../stores/LocalAccounts/LocalAccountsStore';
import ConsoleSamlAccessStore from '../stores/ConsoleSamlAccessStore/ConsoleSamlAccessStore';
import PolicyMigrationStore from '../stores/PolicyMigrationStore';
import ExitMessageStore from '../stores/ExitMessageStore';

class GAController extends Component {
    static propTypes = {
        emailReg: EmailRegStore.PropType.isRequired,
        ipReg: IpRegStore.PropType.isRequired,
        policy: PolicyStore.PropType.isRequired,
        reportingApi: ReportingApiStore.PropType.isRequired,
        authenticationConfiguration: AuthenticationConfigurationStore.PropType.isRequired,
        browserBarOptions: BrowserBarOptionsStore.PropType.isRequired,
        customPages: CustomPagesStore.PropType.isRequired,
        localAccounts: LocalAccountsStore.PropType.isRequired,
        consoleSamlAccess: ConsoleSamlAccessStore.PropType.isRequired,
        policyMigration: PolicyMigrationStore.PropType.isRequired,
        exitMessage: ExitMessageStore.PropType.isRequired,
        customizeUI: CustomizeUIStore.PropType.isRequired,
        console: ConsoleStore.PropType.isRequired,
    };

    componentDidMount() {
        const {
            console,
            emailReg,
            ipReg,
            reportingApi,
            policy,
            authenticationConfiguration,
            browserBarOptions,
            customPages,
            localAccounts,
            consoleSamlAccess,
            policyMigration,
            exitMessage,
            customizeUI,
        } = this.props;
        const { browsingRoles, redirectRules, exclusionRules, contentRules, customCategories, accessCodes } = policy;
        const { registrationNotice } = customPages;
        const customizeUIEnabled = console.isMirrorFeatureEnabled('CUSTOMIZE_UI');

        const routs = {
            policies: {
                browsing_roles: [{
                    func: () => browsingRoles.policyTable.policyModal.mode,
                    newPath: () => browsingRoles.policyTable.policyModal.mode,
                }, {
                    func: () => browsingRoles.policyTable.priorityChangeModal.isOpen,
                    newPath: () => 'change_priority'
                }],
                redirect_rules: [{
                    func: () => redirectRules.policyTable.policyModal.mode,
                    newPath: () => redirectRules.policyTable.policyModal.mode,
                }, {
                    func: () => redirectRules.policyTable.priorityChangeModal.isOpen,
                    newPath: () => 'change_priority'
                }],
                exclusion_rules: [{
                    func: () => exclusionRules.policyTable.policyModal.mode,
                    newPath: () => exclusionRules.policyTable.policyModal.mode,
                }, {
                    func: () => exclusionRules.policyTable.priorityChangeModal.isOpen,
                    newPath: () => 'change_priority'
                }],
                content_rules: [{
                    func: () => contentRules.policyTable.policyModal.mode,
                    newPath: () => contentRules.policyTable.policyModal.mode,
                }, {
                    func: () => contentRules.policyTable.priorityChangeModal.isOpen,
                    newPath: () => 'change_priority'
                }],
                custom_categories: {
                    func: () => customCategories.policyTable.policyModal.mode,
                    newPath: () => customCategories.policyTable.policyModal.mode,
                },
                access_codes: {
                    func: () => accessCodes.policyTable.policyModal.mode,
                    newPath: () => accessCodes.policyTable.policyModal.mode,
                },
            },
            product_settings: {
                reporting_api: {
                    func: () => reportingApi.deleteConfirmationModal.isOpen,
                    newPath: () => 'delete_api_key',
                },
                general_options: [{
                    func: () => emailReg.domainsModal.isOpen,
                    newPath: () => 'edit_email_domains',
                }, {
                    func: () => ipReg.modal.isOpen,
                    newPath: () => 'edit_ip_ranges',
                }],
                saml_sso_configuration: {
                    func: () => authenticationConfiguration.editModal.isOpen,
                    newPath: () => 'edit',
                },
                new_policies_migration: {
                    func: () => {
                        if (policyMigration.modalIsOpen.getValue() && policyMigration.viewedStep.getValue()) {
                            return policyMigration.viewedStep.getValue();
                        }
                        return null;
                    },
                    newPath: () => 'edit_step_' + policyMigration.viewedStep.getValue()
                }
            },
            customize_ui: customizeUIEnabled ? customizeUI.getItemStores().reduce((acc, itemStore) => {
                const name = camelCase(itemStore.metadata.name);
                acc[`${name}Edit`] = {
                    func: () => itemStore.editModal.isOpen,
                    newPath: () => `${name}/edit`,
                };
                acc[`${name}Preview`] = {
                    func: () => customizeUI.readOnlyModal.getValue() === itemStore,
                    newPath: () => `${name}/preview`,
                };
                return acc;
            }, {}) : {
                browser_bar: {
                    func: () => browserBarOptions.editModal.isOpen,
                    newPath: () => 'edit',
                },
                registration_notice: {
                    func: () => registrationNotice.isModalOpen.isOpen,
                    newPath: () => 'edit',
                },
                exit_message: {
                    func: () => exitMessage.editModal.isOpen,
                    newPath: () => 'edit',
                }
            },
            administrators: {
                local_accounts: {
                    func: () => localAccounts.admins.userModal.mode,
                    newPath: () => localAccounts.admins.userModal.mode,
                },
                saml_sso_access: [{
                    func: () => consoleSamlAccess.userAccess.userModal.mode,
                    newPath: () => `users/${consoleSamlAccess.userAccess.userModal.mode}`,
                }, {
                    func: () => consoleSamlAccess.roleAccess.roleModal.modal.isOpen,
                    newPath: () => 'roles/edit',
                }]
            },
        };

        Object.getOwnPropertyNames(routs).forEach((routName) =>
            Object.values(routs[routName]).forEach((funcAndPathList) => {
                funcAndPathList = Array.isArray(funcAndPathList) ? funcAndPathList : [funcAndPathList];
                funcAndPathList.forEach((funcAndPath) => reaction(funcAndPath.func, (isModify) => {
                    if (window.location.pathname.startsWith(`/console/${routName}`)) {
                        if (funcAndPath.previousPath) {
                            window.history.replaceState(null, document.title,
                                window.location.href.replace(new RegExp(`/${funcAndPath.previousPath}$`), ''));
                        }
                        if (isModify) {
                            funcAndPath.previousPath = funcAndPath.newPath();
                            window.history.replaceState(null, document.title,
                                `${window.location.href}/${funcAndPath.previousPath}`);
                        } else {
                            funcAndPath.previousPath = '';
                        }
                    }
                }));
            }));
    }

    render() {
        return this.props.children;
    }
}

export default inject('browserPolicies',
    'emailReg',
    'ipReg',
    'policy',
    'reportingApi',
    'authenticationConfiguration',
    'browserBarOptions',
    'customPages',
    'localAccounts',
    'consoleSamlAccess',
    'policyMigration',
    'exitMessage',
    'customizeUI',
    'console'
)(GAController);
